import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { DISABLED_FEATURES } from '../graphql';

/* ── Domains switched off by default (pilot) — used until the server responds ── */
export const DEFAULT_OFF = [
  'Match',
  'Placement',
  'Companies',
  'Mentors',
  'EAC Seats',
  'Future Aspirations',
  'Personal Finance',
  'Gig / Freelance Projects',
  'Scholarships',
  'Announcements',
  'Campus News',
  'Syllabus',
  'Opportunities',
  'Sports',
  'Food Sharing',
];

/* ── Route path → server domain name (empty string = never hidden) ── */
export const ROUTE_DOMAINS = {
  '/scholarships':  'Scholarships',
  '/announcements': 'Announcements',
  '/campus-news':   'Campus News',
  '/syllabus':      'Syllabus',
  '/opportunities': 'Opportunities',
  '/sports':        'Sports',
  '/food':          'Food Sharing',
  '/cab':           'Cab Sharing',
  '/materials':     'Study Materials',
  '/playlists':     'Curated Playlists',
  '/forums':        'Discussion Forums',
  '/pg-finder':     'PG Partner',
  '/social':        'Student Social',
  '/marketplace':   'Marketplace',
  '/knowledge':     'The Loop',
  '/notes':         '',
  '/flashcards':    '',
};

const FeatureContext = createContext({
  disabled: new Set(DEFAULT_OFF),
  isOff: domain => !!domain && DEFAULT_OFF.includes(domain),
  loading: true,
});

export function FeatureProvider({ children }) {
  const { data, loading, error } = useQuery(DISABLED_FEATURES, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000, // pick up admin toggles without a reload
  });

  const value = useMemo(() => {
    // While loading or on error, fall back to the defaults so nav doesn't flash
    const names = (!error && data?.disabledFeatures) ? data.disabledFeatures : DEFAULT_OFF;
    const disabled = new Set(names);
    return {
      disabled,
      isOff: domain => !!domain && disabled.has(domain),
      loading,
    };
  }, [data, error, loading]);

  return (
    <FeatureContext.Provider value={value}>
      {children}
    </FeatureContext.Provider>
  );
}

export const useFeatures = () => useContext(FeatureContext);
